// src/components/common/ProtectedRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnboarding?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireOnboarding = true 
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-coffee/40 text-sm">Loading...</div>
      </div>
    );
  }
  
  // Redirect to landing if not authenticated
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  
  // Redirect to onboarding if required but not completed
  if (requireOnboarding && !user.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }
  
  // Redirect to dashboard if trying to access onboarding when already completed
  if (location.pathname === '/onboarding' && user.onboardingCompleted) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Redirect admin users away from regular user pages to admin dashboard
  if (user.role === 'admin' && !location.pathname.startsWith('/admin')) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return <>{children}</>;
};

export default ProtectedRoute;